import React, { useRef, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Link, Typography } from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import EditIcon from '@mui/icons-material/Edit'
import ThemeEditor from '../theme-ai/ThemeEditor'
import CodeFramePickerDialog from './CodeFramePickerDialog'
import FrameThemeList from './FrameThemeList'
import StageSection from './StageSection'
import AiAccessTooltip from '../system/AiAccessTooltip'
import DisabledReason from '../system/DisabledReason'
import { toProposedThemes } from '../../hooks/useThemeAITaskAgent'
import type { AutoCoderCode, ThemeAICodeFrame, ThemeAICodeFrameListResponse, ThemeAIGroup, ThemeAITheme } from '../../types/gotypes'
import { THEMEAI_FILE_UPLOAD } from '../../utils/themeAIFeatures'

/** The control that copies a frame the user cannot edit into one of their own. */
export const SAVE_FRAME_COPY_LABEL = 'Save a copy to edit'

interface CodeFrameStageProps {
  frameName?: string
  frameVersion?: number
  frameThemes: ThemeAITheme[]
  frameGroups?: ThemeAIGroup[]
  frameLoading: boolean
  /** Set when the chosen frame is a standard one (#1492): read-only, so it is copied before editing. */
  frameStandard?: ThemeAICodeFrame['standard']
  library: ThemeAICodeFrameListResponse
  libraryLoading: boolean
  saving: boolean
  importing: boolean
  /** False when the task has no saved study context; the page then suggests adding one. */
  hasStudyContext: boolean
  onEditStudyContext: () => void
  /** Opens the agent side pane: to build a frame, or to refine this one. */
  onOpenAgent: (mode: 'build' | 'refine') => void
  /** Why the agent is closed to this person (#1537 AI access, needs level 2), or undefined. */
  agentBlockedReason?: string
  /** Why this person cannot change the task (choose, upload or write a frame for it), or undefined. */
  writeBlockedReason?: string
  /** Why this person cannot edit the chosen frame in place, or undefined. They can save a copy instead. */
  frameEditBlockedReason?: string
  /** Saves a hand-edited theme list: a new frame, or a new version of this one. */
  onSaveThemes: (themes: AutoCoderCode[]) => Promise<void>
  onChooseFrame: (frameId: string, version: number) => Promise<void>
  onUploadFrame: (file: File) => Promise<void>
  onContinue: () => void
  /** Managers may move a library frame to or from the company level. */
  canPromote?: boolean
  onPromoteFrame?: (frameId: string) => void
  onDemoteFrame?: (frameId: string) => void
  /** Copies the frame (a standard one, or one the user cannot edit) into the user's own frames and selects the copy. */
  onSaveCopy?: () => Promise<void>
}

// The code frame is the heart of a task (spec #916 §1.4), so the step shows
// it: every theme with its rule. The agent lives in a side pane that opens
// from here or any step (Adam and Matt, 21 Sep); editing by hand happens in
// place. The two routes always sit side by side.
const CodeFrameStage: React.FC<CodeFrameStageProps> = ({
  frameName,
  frameVersion,
  frameThemes,
  frameGroups,
  frameLoading,
  frameStandard,
  library,
  libraryLoading,
  saving,
  importing,
  hasStudyContext,
  onEditStudyContext,
  onOpenAgent,
  agentBlockedReason,
  writeBlockedReason,
  frameEditBlockedReason,
  onSaveThemes,
  onChooseFrame,
  onUploadFrame,
  onContinue,
  canPromote = false,
  onPromoteFrame,
  onDemoteFrame,
  onSaveCopy,
}) => {
  const [draft, setDraft] = useState<AutoCoderCode[]>()
  const [pickerOpen, setPickerOpen] = useState(false)
  const uploadInput = useRef<HTMLInputElement>(null)
  const hasFrame = frameThemes.length > 0

  const handleUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    try {
      await onUploadFrame(file)
      setDraft(undefined)
    } catch {
      // The hook reports the importer's error (which row, which column).
    }
  }

  const handleChoose = async (frameId: string, version: number) => {
    setPickerOpen(false)
    try {
      await onChooseFrame(frameId, version)
      setDraft(undefined)
    } catch {
      // Reported by the hook.
    }
  }

  const handleSaveCopy = async () => {
    if (!onSaveCopy) return
    try {
      await onSaveCopy()
    } catch {
      // Reported by the hook.
      return
    }
    // The copy is the user's own and selected on the task: open it for editing.
    setDraft(toProposedThemes(frameThemes))
  }

  const handleSaveDraft = async () => {
    if (!draft || draft.length === 0) return
    try {
      await onSaveThemes(draft)
      setDraft(undefined)
    } catch {
      // Reported by the hook; the draft stays on screen.
    }
  }

  const otherSources = (
    <>
      <input ref={uploadInput} type="file" accept=".csv,.xlsx" hidden onChange={handleUploadFile} />
      <CodeFramePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(frame) => handleChoose(frame.id, frame.latest_version || 1)}
        tiers={library}
        loading={libraryLoading}
        canPromote={canPromote}
        onPromote={(frame) => onPromoteFrame?.(frame.id)}
        onDemote={(frame) => onDemoteFrame?.(frame.id)}
      />
    </>
  )

  const studyContextNudge = !hasStudyContext && (
    <Alert severity="info" action={<Button color="inherit" size="small" onClick={onEditStudyContext}>Add it</Button>}>
      Say what the study is about on the Data step. The agent proposes better themes with it, and every run codes with it.
    </Alert>
  )

  if (frameLoading) {
    return (
      <StageSection title="Code frame">
        <CircularProgress size={24} />
      </StageSection>
    )
  }

  if (draft) {
    return (
      <StageSection
        title={hasFrame ? `Edit “${frameName}”` : 'Write the themes'}
        description={
          hasFrame
            ? 'Rename themes, change their rules, add or remove them. Saving creates a new version; earlier runs keep the version they used.'
            : 'Add each theme with a short rule for when to assign it. You can refine them with the agent afterwards.'
        }
        actions={
          <>
            <Button color="info" onClick={() => setDraft(undefined)} disabled={saving}>Cancel</Button>
            <Button variant="contained" color="info" onClick={handleSaveDraft} disabled={saving || draft.length === 0}>
              {saving ? 'Saving…' : hasFrame ? `Save as version ${(frameVersion || 0) + 1}` : `Use these ${draft.length} themes`}
            </Button>
          </>
        }
      >
        <Box sx={{ maxWidth: 880, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          <ThemeEditor
            themes={draft}
            onAddTheme={(theme) => setDraft([...draft, theme])}
            onUpdateTheme={(previous, theme) => setDraft(draft.map((existing) => (existing.code === previous ? theme : existing)))}
            onDeleteTheme={(code) => setDraft(draft.filter((theme) => theme.code !== code))}
          />
        </Box>
      </StageSection>
    )
  }

  if (!hasFrame) {
    return (
      <StageSection
        title="Build a code frame"
        description="The themes every response will be coded against. Most people start with the agent: it reads a sample of the responses and proposes themes you can change."
      >
        {otherSources}
        {studyContextNudge}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <AiAccessTooltip reason={agentBlockedReason}>
            <Button variant="contained" color="info" size="large" startIcon={<AutoAwesomeIcon />} onClick={() => onOpenAgent('build')} disabled={!!agentBlockedReason}>
              Work with the agent
            </Button>
          </AiAccessTooltip>
          <DisabledReason reason={writeBlockedReason}>
            <Button color="info" startIcon={<EditIcon />} onClick={() => setDraft([])}>
              Write the themes yourself
            </Button>
          </DisabledReason>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Or{' '}
          <DisabledReason reason={writeBlockedReason}>
            <Link component="button" color="info.main" onClick={() => setPickerOpen(true)} sx={{ verticalAlign: 'baseline' }}>
              choose an existing frame
            </Link>
          </DisabledReason>
          {THEMEAI_FILE_UPLOAD ? (
            <>
              {' '}or{' '}
              <DisabledReason reason={writeBlockedReason}>
                <Link component="button" color="info.main" onClick={() => uploadInput.current?.click()} sx={{ verticalAlign: 'baseline' }} disabled={importing}>
                  {importing ? 'importing…' : 'upload one'}
                </Link>
              </DisabledReason>{' '}
              (CSV or Excel, one theme per row).
            </>
          ) : '.'}
        </Typography>
      </StageSection>
    )
  }

  return (
    // The page header already names the task and the step, so the frame is
    // named once, here, not as a third title (Matt, 24 Sep, #1523 item 8).
    <StageSection
      description={`${frameName ? `“${frameName}” · ` : ''}${frameStandard ? 'standard code frame, the same for every client' : `version ${frameVersion ?? 1}`} · ${frameThemes.length} theme${frameThemes.length === 1 ? '' : 's'}. Every response is coded against these themes.`}
      actions={
        <Button variant="contained" color="info" onClick={onContinue}>
          Continue to run
        </Button>
      }
    >
      {otherSources}
      {studyContextNudge}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: -1 }}>
        <AiAccessTooltip reason={agentBlockedReason}>
          <Button color="info" startIcon={<AutoAwesomeIcon />} onClick={() => onOpenAgent('refine')} disabled={!!agentBlockedReason}>Work with the agent</Button>
        </AiAccessTooltip>
        {!frameStandard && (
          // Changing the task comes first: without it, no copy helps either.
          <DisabledReason reason={writeBlockedReason ?? frameEditBlockedReason}>
            <Button color="info" startIcon={<EditIcon />} onClick={() => setDraft(toProposedThemes(frameThemes))}>Edit themes</Button>
          </DisabledReason>
        )}
        {(frameStandard || frameEditBlockedReason) && (
          // Standard frames are read-only: the same for every client. A
          // colleague's frame is theirs. Editing starts from the user's own copy.
          <DisabledReason reason={writeBlockedReason}>
            <Button color="info" startIcon={<EditIcon />} onClick={handleSaveCopy} disabled={saving}>
              {SAVE_FRAME_COPY_LABEL}
            </Button>
          </DisabledReason>
        )}
        <DisabledReason reason={writeBlockedReason}>
          <Button color="info" onClick={() => setPickerOpen(true)}>Choose a different frame</Button>
        </DisabledReason>
        {THEMEAI_FILE_UPLOAD && (
          <DisabledReason reason={writeBlockedReason}>
            <Button color="info" onClick={() => uploadInput.current?.click()} disabled={importing}>
              {importing ? 'Importing…' : 'Upload a frame'}
            </Button>
          </DisabledReason>
        )}
      </Box>
      <Box sx={{ maxWidth: 880 }}>
        <FrameThemeList themes={frameThemes} groups={frameGroups} />
      </Box>
    </StageSection>
  )
}

export default CodeFrameStage
